import {
  CreateGroupSurveyRequest,
  CreateTcPayload,
  SurveyListing,
} from '../../../shared/services/buyer-api/survey.interface';
import { BulkEditPayloadValue } from '../types';
import { PatchSurveyInterface } from '@purespectrum1/ui/marketplace/shared/interfaces/survey.interface';
import { ProjectManagers } from 'src/app/operator/user-service/user.interface';

type RequestType =
  | 'patch-surveys'
  | 'delete-surveys'
  | 'group-surveys'
  | 'create-tc-replica';
type PayloadMap = {
  'patch-surveys': PatchSurveyInterface[];
  'delete-surveys': [];
  'group-surveys': CreateGroupSurveyRequest;
  'create-tc-replica': CreateTcPayload;
};

export interface BulkRequestMap<T, K extends RequestType> {
  survey(): number;
  value(): T;
  type(): K;
  payload(): PayloadMap[K];
  update(value: BulkEditPayloadValue): BulkRequestMap<T, K>;
}

export type BulkPatchSurveyRequestConfig<T> = BulkRequestMap<
  T,
  'patch-surveys'
>;
export type BulkDeleteSurveyRequestConfig<T> = BulkRequestMap<
  T,
  'delete-surveys'
>;
export type BulkGroupSurveysRequestConfig<T> = BulkRequestMap<
  T,
  'group-surveys'
>;

export type BulkCreateTcReplicaRequestConfig<T> = BulkRequestMap<
  T,
  'create-tc-replica'
>;

export type BulkRequestConfig<T> =
  | BulkPatchSurveyRequestConfig<T>
  | BulkDeleteSurveyRequestConfig<T>
  | BulkGroupSurveysRequestConfig<T>
  | BulkCreateTcReplicaRequestConfig<T>;

export class BulkPayloadValueCasting {
  constructor(private readonly _value: BulkEditPayloadValue) {}

  public number(): number {
    if (typeof this._value === 'string') {
      return parseInt(this._value, 10);
    }

    if (typeof this._value === 'number') {
      return this._value;
    }

    throw new Error('Unable to parse value');
  }

  public float(): number {
    if (typeof this._value === 'string') {
      return parseFloat(this._value);
    }

    if (typeof this._value === 'number') {
      return this._value;
    }

    throw new Error('Unable to parse value');
  }

  public string(tryConvert = false): string {
    if (typeof this._value === 'string') {
      return this._value;
    }

    if (tryConvert) {
      return this._value.toString();
    }

    throw new Error('Unable to convert value to string');
  }

  public manager(): ProjectManagers {
    if (typeof this._value === 'object' && 'id' in this._value) {
      return this._value as ProjectManagers;
    }

    throw new Error('Unable to cast to project manager');
  }

  public groupSurveys(): CreateGroupSurveyRequest {
    if (typeof this._value === 'object' && 'group_title' in this._value) {
      return this._value as CreateGroupSurveyRequest;
    }

    throw new Error('Unable to cast to group surveys');
  }
}

abstract class BulkPatchSurveysRequestBase<T>
  implements BulkPatchSurveyRequestConfig<T>
{
  constructor(private readonly _survey: number, private readonly _value: T) {}

  public survey(): number {
    return this._survey;
  }

  public value(): T {
    return this._value;
  }

  public type(): 'patch-surveys' {
    return 'patch-surveys';
  }

  abstract payload(): PatchSurveyInterface[];
  abstract update(value: BulkEditPayloadValue): BulkPatchSurveyRequestConfig<T>;
}

abstract class BulkDeleteSurveysRequestBase<T>
  implements BulkDeleteSurveyRequestConfig<T>
{
  constructor(private readonly _survey: number, private readonly _value: T) {}

  public survey(): number {
    return this._survey;
  }

  public value(): T {
    return this._value;
  }

  public type(): 'delete-surveys' {
    return 'delete-surveys';
  }

  abstract payload(): [];
  abstract update(
    value: BulkEditPayloadValue
  ): BulkDeleteSurveyRequestConfig<T>;
}

abstract class BulkGroupSurveysRequestBase<T>
  implements BulkGroupSurveysRequestConfig<T>
{
  constructor(private readonly _survey: number, private readonly _value: T) {}

  public survey(): number {
    return this._survey;
  }

  public value(): T {
    return this._value;
  }

  public type(): 'group-surveys' {
    return 'group-surveys';
  }

  abstract payload(): CreateGroupSurveyRequest;
  abstract update(
    value: BulkEditPayloadValue
  ): BulkGroupSurveysRequestConfig<T>;
}

export class BulkEditCpiRequest extends BulkPatchSurveysRequestBase<number> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).float());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/offer_price',
        value: this.value(),
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<number> {
    return new BulkEditCpiRequest(this.survey(), value);
  }
}

export class BulkEditMarginRequest extends BulkPatchSurveysRequestBase<number> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).number());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/margin',
        value: this.value(),
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<number> {
    return new BulkEditMarginRequest(this.survey(), value);
  }
}

export class BulkEditProjectManagerRequest extends BulkPatchSurveysRequestBase<ProjectManagers> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).manager());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/bpm',
        value: this.value().id,
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<ProjectManagers> {
    return new BulkEditProjectManagerRequest(this.survey(), value);
  }
}

export class BulkEditAccountManagerRequest extends BulkPatchSurveysRequestBase<ProjectManagers> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).manager());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/pm',
        value: this.value().id,
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<ProjectManagers> {
    return new BulkEditAccountManagerRequest(this.survey(), value);
  }
}

export class BulkEditBillingNumberRequest extends BulkPatchSurveysRequestBase<string> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).string());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/billing_id',
        value: this.value(),
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<string> {
    return new BulkEditBillingNumberRequest(this.survey(), value);
  }
}

export class BulkEditStatusRequest extends BulkPatchSurveysRequestBase<number> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).number());
  }

  public payload(): PatchSurveyInterface[] {
    return [
      {
        op: 'replace',
        path: '/ps_survey_status',
        value: this.value(),
      },
    ];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkPatchSurveysRequestBase<number> {
    return new BulkEditStatusRequest(this.survey(), value);
  }
}

export class BulkDeleteDraftRequest extends BulkDeleteSurveysRequestBase<number> {
  constructor(survey: number, value: BulkEditPayloadValue) {
    super(survey, new BulkPayloadValueCasting(value).number());
  }

  public payload(): [] {
    return [];
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkDeleteSurveysRequestBase<number> {
    return new BulkDeleteDraftRequest(this.survey(), value);
  }
}

abstract class BulkCreateTcReplicaRequestBase<T>
  implements BulkCreateTcReplicaRequestConfig<T>
{
  constructor(private readonly _survey: number, private readonly _value: T) {}

  public survey(): number {
    return this._survey;
  }

  public value(): T {
    return this._value;
  }

  public type(): 'create-tc-replica' {
    return 'create-tc-replica';
  }

  abstract payload(): CreateTcPayload;
  abstract update(
    value: BulkEditPayloadValue
  ): BulkCreateTcReplicaRequestConfig<T>;
}

export class BulkGroupSurveysRequest extends BulkGroupSurveysRequestBase<CreateGroupSurveyRequest> {
  constructor(survey: number, value: CreateGroupSurveyRequest) {
    super(survey, new BulkPayloadValueCasting(value).groupSurveys());
  }

  public payload(): CreateGroupSurveyRequest {
    return this.value();
  }

  public update(
    value: BulkEditPayloadValue
  ): BulkGroupSurveysRequestBase<CreateGroupSurveyRequest> {
    return new BulkGroupSurveysRequest(
      this.survey(),
      value as CreateGroupSurveyRequest
    );
  }
}

export class BulkCreateTcReplicaRequest extends BulkCreateTcReplicaRequestBase<string> {
  constructor(survey: number) {
    super(survey, 'launch');
  }

  public payload(): CreateTcPayload {
    return {
      channelSurveyName: 'TC-R_Bulk_Replica',
      channelType: 'REP',
      payload: {},
      qbpQuota: {},
      copyTargetingFromParent: true,
    };
  }

  public update(_value: BulkEditPayloadValue): BulkCreateTcReplicaRequest {
    return new BulkCreateTcReplicaRequest(this.survey());
  }
}

export class BulkOperationRequestFormatter {
  constructor(
    private readonly _value: BulkEditPayloadValue,
    private readonly _type: string,
    private readonly _surveys: SurveyListing[]
  ) {}

  public requests(): BulkRequestConfig<BulkEditPayloadValue>[] {
    return this._format();
  }

  private _format(): BulkRequestConfig<BulkEditPayloadValue>[] {
    switch (this._type) {
      case 'cpi':
        return this._surveys.map(
          (survey) => new BulkEditCpiRequest(survey.surveyId, this._value)
        );
      case 'projectManager':
        return this._surveys.map(
          (survey) =>
            new BulkEditProjectManagerRequest(survey.surveyId, this._value)
        );
      case 'accountManager':
        return this._surveys.map(
          (survey) =>
            new BulkEditAccountManagerRequest(survey.surveyId, this._value)
        );
      case 'billingNumber':
        return this._surveys.map(
          (survey) =>
            new BulkEditBillingNumberRequest(survey.surveyId, this._value)
        );
      case 'status':
        return this._surveys.map(
          (survey) => new BulkEditStatusRequest(survey.surveyId, this._value)
        );
      case 'margin':
        return this._surveys.map(
          (survey) => new BulkEditMarginRequest(survey.surveyId, this._value)
        );
      case 'deleteDraft':
        return this._surveys.map(
          (survey) => new BulkDeleteDraftRequest(survey.surveyId, this._value)
        );
      case 'groupSurveys':
        return [
          new BulkGroupSurveysRequest(0, {
            survey_ids: this._surveys.map((s) => s.surveyId),
            group_title: this._value as string,
          }),
        ];
      case 'tcReplicaLaunch':
        return this._surveys.map(
          (survey) => new BulkCreateTcReplicaRequest(survey.surveyId)
        );
      default:
        throw new Error(`Formatter for type '${this._type}' not found.`);
    }
  }
}
